/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { API } from "aws-amplify";
import { persistInterventionRoi } from "../../../graphql/mutations";
import {
  getRoiCalculation,
  listRoiCalculationsByBaseline,
} from "../../../graphql/queries";
import { RoiCalculation } from "../../../graphql/types";
import { RoiFormInputs } from "../helpers/roiOutMapper";
import { parseAwsJson } from "../helpers/roiStageData";

interface PersistInterventionRoiArgs {
  customerId: string;
  farmId: string;
  animalSystemId: string;
  oldName: string;
  inputs: RoiFormInputs;
}

type PersistInterventionRoiResult = Promise<{
  data?: { persistInterventionRoi?: RoiCalculation | null };
}>;

export const persistInterventionRoiCalculation = async ({
  customerId,
  farmId,
  animalSystemId,
  oldName,
  inputs,
}: PersistInterventionRoiArgs): Promise<RoiCalculation | null> => {
  const result = await (API.graphql({
    query: persistInterventionRoi,
    variables: {
      customerId,
      farmId,
      animalSystemId,
      oldName,

      inputs: JSON.stringify(inputs),
    },
  }) as PersistInterventionRoiResult);

  return result?.data?.persistInterventionRoi ?? null;
};

interface FetchRoiCalculationArgs {
  customerId: string;
  baselineReference: string;
  interventionReference: string;
}

type GetRoiCalculationResult = Promise<{
  data?: { getRoiCalculation?: RoiCalculation | null };
}>;

export const fetchRoiCalculation = async ({
  customerId,
  baselineReference,
  interventionReference,
}: FetchRoiCalculationArgs): Promise<RoiCalculation | null> => {
  const result = await (API.graphql({
    query: getRoiCalculation,
    variables: { customerId, baselineReference, interventionReference },
  }) as GetRoiCalculationResult);

  return result?.data?.getRoiCalculation ?? null;
};

export interface RoiCalculationSummary {
  interventionReference: string;
  referenceId: string;
  value: number | null;
  currency?: string;
}

type ListRoiCalculationsByBaselineResult = Promise<{
  data?: {
    listRoiCalculationsByBaseline?: Array<RoiCalculation | null> | null;
  };
}>;

export const fetchRoiCalculationsByBaseline = async ({
  customerId,
  baselineReference,
}: {
  customerId: string;
  baselineReference: string;
}): Promise<RoiCalculationSummary[]> => {
  const response = await (API.graphql({
    query: listRoiCalculationsByBaseline,
    variables: { customerId, baselineReference },
  }) as ListRoiCalculationsByBaselineResult);

  const calculations = response?.data?.listRoiCalculationsByBaseline ?? [];

  return calculations
    .filter(
      (calc): calc is RoiCalculation =>
        Boolean(calc?.interventionReference) && Boolean(calc?.SK)
    )
    .map((calc) => {
      const parsed = parseAwsJson<{
        economics?: { currency?: unknown };
        kpis?: { roi?: { absolute?: unknown } };
      }>(calc.result);
      const economics = parsed?.economics ?? {};
      const roi = parsed?.kpis?.roi ?? {};
      return {
        interventionReference: calc.interventionReference as string,
        referenceId: calc.SK as string,
        value: typeof roi.absolute === "number" ? roi.absolute : null,
        currency:
          typeof economics.currency === "string"
            ? economics.currency
            : undefined,
      };
    });
};

export const extractGraphqlErrorMessage = (
  err: unknown
): string | undefined => {
  const errors = (err as { errors?: Array<{ message?: string }> } | null)
    ?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return (
      errors
        .map((e) => e?.message)
        .filter(Boolean)
        .join("; ") || undefined
    );
  }
  if (err instanceof Error) return err.message;
  return undefined;
};
